import React, { useEffect, useState } from 'react';
import { useForm, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import * as axiosInstance from '../utils/axiosinstace';
import AuthLayout from '../auth/AuthLayout';
import AuthField from '../auth/AuthField';
import { useAuthMotion } from '../auth/authMotion';
import styles from '../auth/AuthLayout.module.css';

// Mirrors the window the API issues (routes/forgotpassword.js). If you change
// one, change the other — this countdown is only a display of the server's
// rule, it does not enforce anything.
const OTP_SECONDS = 180;
const OTP_LOW_WATER = 30; // when the chip turns amber
const OTP_PATTERN = /^[0-9]{6}$/;

// Password rules, mirrored by auth:forgot.passwordHint.
const isStrongPassword = (value) =>
    /^.{8,20}$/.test(value) &&
    /[!@#$%^&*()_+{}[\]:;<>,.?~\\-]/.test(value) &&
    /[A-Z]/.test(value);

// How long is left on the OTP, and how close to expiry.
//
// The previous implementation re-created its interval on every tick (the
// effect depended on the seconds value) and read a stale `otpTimer` inside the
// callback, so expiry fired a tick late. One interval, keyed only to whether
// the countdown is live, is both simpler and correct.
const useCountdown = (live, seconds) => {
    const [left, setLeft] = useState(seconds);

    useEffect(() => {
        if (!live) {
            setLeft(seconds);
            return undefined;
        }
        const id = setInterval(() => setLeft((n) => (n <= 1 ? 0 : n - 1)), 1000);
        return () => clearInterval(id);
    }, [live, seconds]);

    return left;
};

const ForgotPassword = () => {
    const { t } = useTranslation(['auth', 'common', 'forms']);
    const v = useAuthMotion();
    const {
        register,
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({ mode: 'onChange' });

    // `sent` is the step: false = ask for the email, true = OTP + new password.
    const [sent, setSent] = useState(false);
    const [result, setResult] = useState(null); // { error: bool, message: string }

    const secondsLeft = useCountdown(sent, OTP_SECONDS);
    const expired = sent && secondsLeft === 0;
    const password = watch('password');
    const otp = watch('otp');

    // The password fields only appear once a full 6-digit code has been
    // entered. Latched, so backspacing a digit doesn't yank the fields away
    // (and the password already typed into them) out from under the user.
    const [codeEntered, setCodeEntered] = useState(false);
    useEffect(() => {
        if (OTP_PATTERN.test(otp || '')) setCodeEntered(true);
    }, [otp]);

    // An expired code can't be used, so drop back to step one instead of
    // leaving the user on a form whose submit is guaranteed to fail. (The old
    // code kept the email input disabled here, which was a dead end.)
    useEffect(() => {
        if (!expired) return;
        setSent(false);
        setCodeEntered(false);
        setResult({ error: true, message: t('auth:forgot.otpExpired') });
    }, [expired, t]);

    const onSubmit = async (data) => {
        setResult(null);
        try {
            if (!sent) {
                const response = await axiosInstance.getEmailValidation(data.username);
                if (response.status) setSent(true);
                else setResult({ error: true, message: response.message });
                return;
            }

            const response = await axiosInstance.udpatePassword(data);
            if (response.status) {
                setSent(false);
                setCodeEntered(false);
                reset();
            }
            setResult({ error: !response.status, message: response.message });
        } catch (err) {
            setResult({ error: true, message: (err && err.message) || t('auth:login.genericError') });
        }
    };

    const submitLabel = sent ? t('auth:setPassword.title') : t('auth:forgot.send');
    const busyLabel = sent ? t('common:actions.saving') : t('auth:forgot.sending');

    return (
        <AuthLayout title={t('auth:forgot.title')} subtitle={t('auth:forgot.subtitle')}>
            <motion.form onSubmit={handleSubmit(onSubmit)} noValidate>
                <AnimatePresence initial={false}>
                    {result && (
                        <motion.div
                            key='forgot-result'
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            style={{ overflow: 'hidden' }}
                        >
                            <div
                                className={`${styles.banner} ${result.error ? '' : styles.bannerOk}`}
                                role='alert'
                            >
                                {result.message}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div variants={v.formContainer} initial='hidden' animate='visible'>
                    <motion.div variants={v.item}>
                        <AuthField
                            id='forgot-email'
                            label={t('common:fields.email')}
                            icon='fa fa-envelope'
                            type='email'
                            autoComplete='email'
                            disabled={sent}
                            placeholder={t('auth:forgot.emailPlaceholder')}
                            error={
                                errors.username &&
                                (errors.username.message || t('auth:validation.enterUserName'))
                            }
                            {...register('username', {
                                required: true,
                                pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                    message: t('auth:validation.invalidEmail'),
                                },
                            })}
                        />
                    </motion.div>

                    {sent && (
                        <>
                            <div className={`${styles.otpChip} ${secondsLeft <= OTP_LOW_WATER ? styles.otpChipLow : ''}`}>
                                <span>{t('auth:forgot.otpExpiresIn', { seconds: secondsLeft })}</span>
                                <span
                                    className={styles.otpTrack}
                                    style={{
                                        '--otp-progress': `${(Math.max(secondsLeft, 0) / OTP_SECONDS) * 100}%`,
                                    }}
                                />
                            </div>

                            <Controller
                                name='otp'
                                control={control}
                                rules={{
                                    required: t('forms:requiredField.invalidOtp'),
                                    pattern: { value: /^[0-9]{6}$/, message: t('auth:validation.otpFormat') },
                                }}
                                render={({ field }) => (
                                    <AuthField
                                        id='forgot-otp'
                                        label={t('auth:forgot.otpLabel')}
                                        icon='fas fa-hashtag'
                                        type='password'
                                        canReveal
                                        revealLabel={t('auth:login.showPassword')}
                                        hideRevealLabel={t('auth:login.hidePassword')}
                                        inputMode='numeric'
                                        autoComplete='one-time-code'
                                        placeholder={t('auth:forgot.otpPlaceholder')}
                                        error={errors.otp && errors.otp.message}
                                        {...field}
                                    />
                                )}
                            />

                            {codeEntered && (
                              <motion.div
                                initial={v.reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: v.reduced ? 0.18 : 0.35, ease: 'easeOut' }}
                              >
                            <Controller
                                name='password'
                                control={control}
                                rules={{
                                    required: t('auth:validation.enterAPassword'),
                                    validate: (value) =>
                                        isStrongPassword(value) || t('auth:validation.passwordStrength'),
                                }}
                                render={({ field }) => (
                                    <AuthField
                                        id='forgot-password'
                                        label={t('common:fields.password')}
                                        icon='fas fa-lock'
                                        type='password'
                                        canReveal
                                        revealLabel={t('auth:login.showPassword')}
                                        hideRevealLabel={t('auth:login.hidePassword')}
                                        autoComplete='new-password'
                                        placeholder={t('auth:login.passwordPlaceholder')}
                                        // Shown inline rather than behind a tooltip icon: these are
                                        // rules you need while typing, not on hover.
                                        hint={t('auth:forgot.passwordHint')}
                                        error={errors.password && errors.password.message}
                                        {...field}
                                    />
                                )}
                            />

                            <Controller
                                name='confirmPassword'
                                control={control}
                                rules={{
                                    required: t('auth:validation.confirmYourPassword'),
                                    validate: (value) =>
                                        value === password || t('auth:validation.passwordsDoNotMatch'),
                                }}
                                render={({ field }) => (
                                    <AuthField
                                        id='forgot-confirm'
                                        label={t('auth:forgot.confirmPassword')}
                                        icon='fas fa-lock'
                                        type='password'
                                        canReveal
                                        revealLabel={t('auth:login.showPassword')}
                                        hideRevealLabel={t('auth:login.hidePassword')}
                                        autoComplete='new-password'
                                        placeholder={t('auth:forgot.confirmPassword')}
                                        error={errors.confirmPassword && errors.confirmPassword.message}
                                        {...field}
                                    />
                                )}
                            />
                              </motion.div>
                            )}
                        </>
                    )}

                    <motion.div variants={v.item}>
                        <motion.button
                            type='submit'
                            className={styles.submit}
                            disabled={isSubmitting || (sent && !codeEntered)}
                            aria-busy={isSubmitting}
                            whileHover={isSubmitting ? undefined : { scale: 1.02 }}
                            whileTap={isSubmitting ? undefined : { scale: 0.98 }}
                        >
                            {isSubmitting && !v.reduced && (
                                <span className={styles.spinner} aria-hidden='true' />
                            )}
                            {isSubmitting ? busyLabel : submitLabel}
                        </motion.button>
                    </motion.div>

                    <motion.div className={styles.backRow} variants={v.item}>
                        <Link to='/login' className={styles.link}>
                            {t('auth:forgot.backToLogin')}
                        </Link>
                    </motion.div>
                </motion.div>
            </motion.form>
        </AuthLayout>
    );
};

export default ForgotPassword;
